import { useEffect, useRef } from 'react';
import { MENU_TABS, menuItems, type Category, type LineConfig, type MenuItem } from '@tikkas/shared';
import { ART, IC, Svg, imageFor } from '../art/icons.js';
import { newBuilder, safePrice } from '../lib/bag.js';
import { fmt } from '../lib/format.js';
import { useNav } from '../lib/router.js';
import { featureOn, useStore } from '../lib/store.js';
import { AccountButton, BagBar } from '../components/TabBar.js';
import { useLook } from '../lib/look.js';
import { accountIsTab } from '../lib/appearance.js';
import { pickupText, LineThumb } from '../components/Sheets.js';
import { sizeTitle } from '@tikkas/shared';
import { track } from '../lib/events.js';
import { OrderingClosed } from '../components/ui.js';

const MENU_INTRO: Partial<Record<Category, string>> = { bowls: 'Pick a base, then your tikkas.', just: 'Just the tikkas, boxed.' };
/** The first category tab when the customer has usuals (Saif, 22 Oct): saved orders and favourites, selected on landing. */
const USUALS_TAB = 'usuals';

/** The heart on a menu item: keeps it in "Your usuals" at the top of the Order tab (on this phone). */
export function FavButton({ itemId, name, className = '' }: { itemId: string; name: string; className?: string }) {
  const s = useStore();
  const on = s.favourites.includes(itemId);
  return (
    <button type="button" className={`favbtn ${on ? 'on' : ''} ${className}`} aria-pressed={on} aria-label={on ? `Remove ${name} from favourites` : `Add ${name} to favourites`} onClick={(e) => { e.stopPropagation(); s.toast(s.toggleFavourite(itemId) ? `${name} is in your favourites` : `${name} removed from favourites`); }}>
      <Svg html={on ? IC.heartOn : IC.heart} />
    </button>
  );
}

/** Saved orders and favourites: the cards of the "Your usuals" tab. A saved order goes straight in the bag; a favourite opens like it does on the menu. */
function UsualCards({ open, quick }: { open: (it: MenuItem) => void; quick: (it: MenuItem) => void }) {
  const s = useStore();
  const { navigate } = useNav();
  const menu = s.menuData?.menu;
  if (!menu) return null;
  const saved = s.me?.savedOrders ?? [];
  const favs = s.favourites.map((id) => menu.items[id]).filter((it): it is MenuItem => !!it);
  const pic = (it: MenuItem | undefined) => (it && (it.image || it.photoUrl) ? <img src={imageFor(it.image, it.photoUrl)} alt="" /> : <span className="upic"><Svg html={(it && ART[it.id]) ?? ART.tots!} /></span>);
  const reorder = (lines: { config: LineConfig; qty: number }[], name: string) => {
    for (const l of lines) s.addLine({ ...l.config, reward: null }, l.qty);
    s.toast(`${name} added`);
    navigate('/bag');
  };
  return (
    <div className="card rows">
      {saved.map((f) => {
        const first = f.lines.find((l) => menu.items[l.config.itemId]?.hasTikkas) ?? f.lines[0];
        const it = first ? menu.items[first.config.itemId] : undefined;
        const total = f.lines.reduce((a, l) => a + (safePrice(menu, { id: 'x', config: { ...l.config, reward: null }, qty: l.qty })?.linePence ?? 0), 0);
        return <button type="button" className="r usualr" key={f.id} onClick={() => reorder(f.lines, f.name)}>{pic(it)}<span className="grow col" style={{ gap: 2, minWidth: 0 }}><b className="one">{f.name}</b><span className="small muted one">Saved · {fmt(total)} · {f.lines.reduce((a, l) => a + l.qty, 0)} item{f.lines.reduce((a, l) => a + l.qty, 0) === 1 ? '' : 's'}</span></span><span className="pillbtn line">Reorder</span></button>;
      })}
      {favs.map((it) => <button type="button" className={`r usualr ${it.soldOut ? 'soldrow' : ''}`} key={it.id} disabled={!!it.soldOut} onClick={() => (it.hasTikkas || it.sizes.length > 1 ? open(it) : quick(it))}>{pic(it)}<span className="grow col" style={{ gap: 2, minWidth: 0 }}><b className="one">{it.name}</b><span className="small muted one">{it.soldOut ? 'Sold out today' : `Favourite · ${it.sizes.length > 1 ? 'from ' : ''}${fmt(it.sizes[0]!.pricePence)}`}</span></span><Svg html={IC.chev} /></button>)}
    </div>
  );
}

export function SideBag() {
  const s = useStore();
  const { navigate } = useNav();
  const { t } = useLook();
  const menu = s.menuData?.menu;
  if (!menu) return null;
  const total = s.bag.reduce((a, l) => a + (safePrice(menu, l)?.linePence ?? 0), 0);
  return (
    <aside className="sidebag">
      <h2 className="h3" style={{ margin: 0 }}>{t('page.bag')}</h2>
      {s.bag.length ? (
        <>
          <div className="col" style={{ gap: 12 }}>
            {s.bag.map((l) => {
              const it = menu.items[l.config.itemId];
              const pr = safePrice(menu, l);
              return <div className="sbl" key={l.id}><LineThumb item={it} config={l.config} /><span className="grow col" style={{ gap: 2, minWidth: 0 }}><b>{it?.hasTikkas && l.config.portions.length ? sizeTitle(menu, l.config) : pr?.name ?? it?.name}</b><span className="small muted">{pr ? `${pr.name} · ${pr.sizeName}` : ''}{l.qty > 1 ? ` × ${l.qty}` : ''}</span></span><span className="tnum" style={{ fontWeight: 700 }}>{fmt(pr?.linePence ?? 0)}</span></div>;
            })}
          </div>
          <div className="ctot"><span>Total</span><b>{fmt(total)}</b></div>
          <button type="button" className="yellow" onClick={() => navigate('/bag')}>{t('button.checkout')}</button>
        </>
      ) : <p className="small muted" style={{ margin: 0 }}>{t('bag.empty_title')}{/[.!?]$/.test(t('bag.empty_title')) ? '' : '.'} {t('bag.empty_body')}</p>}
    </aside>
  );
}

/**
 * The Order tab (CLAUDE.md §4 "Order tab", Saif 22 Oct 2026): the collection time, then Catering and Group order as two
 * tiles (each only while its switch is on), then the category tabs, with "Your usuals" (saved orders and favourites) as
 * the first tab and selected on landing when the customer has any, then the menu.
 */
export function Menu({ laptop }: { laptop: boolean }) {
  const s = useStore();
  const { navigate } = useNav();
  const { v, t } = useLook();
  const menu = s.menuData?.menu;
  const scrollRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const jumpUntil = useRef(0);
  const hasUsuals = !!menu && v.pages.menu.includes('usuals') && ((s.me?.savedOrders.length ?? 0) > 0 || s.favourites.some((id) => !!menu.items[id]));
  const tabs: ReadonlyArray<readonly [string, string]> = hasUsuals ? [[USUALS_TAB, t('order.usuals')], ...MENU_TABS] : MENU_TABS;
  useEffect(() => {
    track('menu_viewed');
  }, []);
  // Category bar sticks to the top and follows the scroll; tapping a category glides to it.
  useEffect(() => {
    const sc = scrollRef.current, bar = barRef.current;
    if (!sc || !bar) return;
    const spy = () => {
      if (Date.now() < jumpUntil.current) return;
      const line = bar.getBoundingClientRect().bottom + 24;
      let cur = tabs[0]![0];
      sc.querySelectorAll<HTMLElement>('.menusec').forEach((sec) => {
        if (sec.getBoundingClientRect().top <= line) cur = sec.dataset.cat as string;
      });
      if (sc.scrollTop + sc.clientHeight >= sc.scrollHeight - 4) cur = tabs[tabs.length - 1]![0];
      bar.classList.toggle('stuck', bar.getBoundingClientRect().top <= sc.getBoundingClientRect().top + 1);
      bar.querySelectorAll<HTMLElement>('.pill').forEach((p) => p.setAttribute('aria-selected', String(p.dataset.tab === cur)));
    };
    sc.addEventListener('scroll', spy, { passive: true });
    spy();
    return () => sc.removeEventListener('scroll', spy);
  }, [menu, hasUsuals]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!menu) return <div className="scroll"><div className="pad"><p className="muted">Loading the menu…</p></div></div>;
  const jump = (cat: string) => {
    const sc = scrollRef.current, sec = sc?.querySelector<HTMLElement>(`#sec-${cat}`), bar = barRef.current;
    if (!sc || !sec || !bar) return;
    jumpUntil.current = Date.now() + 700;
    bar.querySelectorAll<HTMLElement>('.pill').forEach((p) => p.setAttribute('aria-selected', String(p.dataset.tab === cat)));
    sc.scrollTo({ top: sec.getBoundingClientRect().top - sc.getBoundingClientRect().top + sc.scrollTop - bar.offsetHeight - 8, behavior: 'smooth' });
  };
  const open = (it: MenuItem) => {
    s.setBuilder(newBuilder(it.id));
    track('item_viewed', { itemId: it.id });
    navigate('/build');
  };
  const quick = (it: MenuItem) => {
    s.addLine({ itemId: it.id, sizeIndex: 0, portions: [], sauces: [], toppingsOff: [], upgradeIds: [] });
    s.toast(`${it.name} added`);
  };
  const section = (cat: Category) => {
    const items = menuItems(menu, cat);
    if (cat === 'sides' || cat === 'desserts') {
      return (
        <div className="card rows">
          {items.map((it) => (
            <div className={`r simple ${it.soldOut ? 'soldrow' : ''}`} key={it.id}>
              <span className="pic" style={{ background: 'var(--tile-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Svg html={ART[it.id] ?? ART.tots!} style={{ width: 40, height: 40 }} /></span>
              <span className="grow col" style={{ gap: 3 }}><b style={{ fontSize: 16 }}>{it.name}</b><span className="small muted">{it.description}</span>
                <span style={{ fontSize: 14, fontWeight: 700 }}>{fmt(it.sizes[0]!.pricePence)}{it.sizes[1] ? <span className="muted" style={{ fontWeight: 500 }}> · Large {fmt(it.sizes[1].pricePence)}</span> : null}</span></span>
              <FavButton itemId={it.id} name={it.name} className="inrow" />
              {it.soldOut ? <span className="soldpill">Sold out</span> : <button type="button" className="plus" onClick={() => (it.sizes.length > 1 ? open(it) : quick(it))} aria-label={`Add ${it.name}`}>+</button>}
            </div>
          ))}
        </div>
      );
    }
    return (
      <div className="col" style={{ gap: 14 }}>
        {items.map((it) => (
          <div className="itemwrap" key={it.id}>
            <button type="button" className="card item" onClick={() => open(it)} disabled={!!it.soldOut}>
              <span className="imgwrap"><img src={imageFor(it.image, it.photoUrl)} alt="" loading="lazy" />{it.popular && !it.soldOut ? <span className="tag">Popular</span> : null}{it.soldOut ? <span className="tag">Sold out today</span> : null}</span>
              <span className="col" style={{ minWidth: 0 }}><span className="name">{it.name}</span><span className="small muted">{it.description}</span><span className="price">From {fmt(it.sizes[0]!.pricePence)}{it.glutenFree ? <span className="tag">GF</span> : null}</span></span>
            </button>
            <FavButton itemId={it.id} name={it.name} className="onimg" />
          </div>
        ))}
      </div>
    );
  };
  const catering = featureOn(s, 'catering'), group = featureOn(s, 'groupOrder');
  return (
    <>
      <div className="scroll" id="menuscroll" ref={scrollRef}>
        {/* No inline bottom padding: styles.css keeps room for the bar and the View bag pill (html[data-bar][data-bag]). */}
        <div className="pad" style={{ gap: 18 }}>
          <div className="menuhead">
            <div className="pagehead"><h1 className="display">{t('page.order')}</h1>{accountIsTab(v) ? null : <AccountButton />}</div>
            <button type="button" className="pickline" onClick={() => s.openSheet({ kind: 'pickup' })}><span><Svg html={IC.clock} /></span><span className="grow col" style={{ gap: 1, minWidth: 0 }}><b>{pickupText(s)}</b><span className="s">{s.config?.site.address ?? '1 Great Titchfield St'}</span></span><span className="chg">Change</span></button>
          </div>
          <OrderingClosed compact />
          {v.pages.menu.map((id) => {
            if (id === 'usuals') return null; // the "Your usuals" tab, below
            if (id === 'group') {
              // Catering and Group order as two proper tiles (Saif, 22 Oct): each only while its switch is on; one alone takes the row.
              return catering || group ? (
                <div className="moretiles" key={id}>
                  {catering && <button type="button" className="card moretile" onClick={() => navigate('/catering')}><span className="ic"><Svg html={IC.bag} /></span><b>Catering</b><span className="small muted">{t('order.catering_line')}</span></button>}
                  {group && <button type="button" className="card moretile" onClick={() => navigate('/group')}><span className="ic"><Svg html={IC.people} /></span><b>Group order</b><span className="small muted">{t('order.group_line')}</span></button>}
                </div>
              ) : null;
            }
            if (id !== 'menu') return null;
            return [
              <div className="menutabs" id="menutabs" ref={barRef} key="menutabs"><div className="pills" role="tablist" aria-label="Menu sections">
                {tabs.map(([k, l], i) => <button type="button" role="tab" className="pill" aria-selected={i === 0} data-tab={k} key={k} onClick={() => jump(k)}>{l}</button>)}
              </div></div>,
              ...(hasUsuals ? [
                <section className="menusec" id={`sec-${USUALS_TAB}`} data-cat={USUALS_TAB} key={USUALS_TAB}>
                  <div className="col" style={{ gap: 2 }}><h2 className="h2">{t('order.usuals')}</h2><p className="small muted" style={{ margin: 0 }}>Saved orders go straight in the bag; a favourite opens as on the menu.</p></div>
                  <UsualCards open={open} quick={quick} />
                </section>,
              ] : []),
              ...MENU_TABS.map(([k, l]) => (
                <section className="menusec" id={`sec-${k}`} data-cat={k} key={k}>
                  <div className="col" style={{ gap: 2 }}><h2 className="h2">{l}</h2>{MENU_INTRO[k] ? <p className="small muted" style={{ margin: 0 }}>{MENU_INTRO[k]}</p> : null}</div>
                  {section(k)}
                </section>
              )),
            ];
          })}
        </div>
      </div>
      {laptop && <SideBag />}
      {!laptop && <BagBar />}
    </>
  );
}
